import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../../core/store/store';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import { usePanelAccess } from '../../context/PanelAccessContext';
import { REFERENT_PANEL_LINKS } from '../../constants/referentPanelLinks';
import PanelHubGrid, { HubCardItem } from '../../components/PanelHubGrid';

/**
 * Hub « Référents » : même présentation que les autres espaces (grille de cartes).
 */
const PanelReferentHubV1: React.FC = () => {
  const navigate = useNavigate();
  const { t } = usePageTranslation('panel', 'Panel');
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { loading, hasAccessToSection, hasReferentFunction, referentFunctions } = usePanelAccess();
  const roleId = currentUser?.roles_id ?? 0;
  const canEnterHub = hasAccessToSection('salon_ref');

  React.useEffect(() => {
    if (!loading && currentUser && !canEnterHub) {
      navigate('/panel/dashboard', { replace: true });
    }
  }, [loading, currentUser, canEnterHub, navigate]);

  const cards = useMemo((): HubCardItem[] => {
    const reserved = t('sections.referent.reservedForReferent');
    const items: HubCardItem[] = REFERENT_PANEL_LINKS.map((action) => {
      // Le CM (role 1) supervise tous les référents : accès complet, cohérent avec
      // la sidebar (PanelSidebarNav → `if (roleId === 1) return true`). Sans ce bypass,
      // les cartes du hub référent étaient grisées pour un CM non explicitement assigné.
      const canAccess = roleId === 1
        ? true
        : action.allowOnlyCm
          ? false
          : action.allowAllReferents
            ? true
            : hasReferentFunction(action.referentFunction);
      const label = action.labelKey
        ? t(action.labelKey)
        : t(`sections.referent.items.${action.referentFunction.replace(/\s+/g, '_')}`);
      return {
        to: action.path,
        title: label,
        accent: 'cyan' as const,
        disabled: !canAccess,
        disabledHint: canAccess ? undefined : reserved,
      };
    });

    if (referentFunctions.length > 0) {
      items.push({
        to: '/panel/support/team',
        title: t('sections.referent.ticket.button'),
        description: 'Tickets support équipe',
        accent: 'amber',
      });
    }

    return items;
  }, [hasReferentFunction, referentFunctions.length, roleId, t]);

  if (!currentUser || loading) {
    return <p className="text-sm text-slate-400">Chargement de l’espace référent…</p>;
  }

  if (!canEnterHub) {
    return null;
  }

  return (
    <PanelHubGrid
      description="Outils selon vos fonctions référent assignées. Les cartes grisées nécessitent la fonction correspondante."
      cards={cards}
      columns={2}
    />
  );
};

export default PanelReferentHubV1;
